import React, { useState, useEffect, useCallback, useContext } from 'react';
import './Editor.css';
import { autoResize } from '../../utils/editor/autoResize';  // Utility for resizing text areas automatically
import DOMPurify from 'dompurify';  // Utility to sanitize input text
import AppContext from '../../AppContext';
import hash from 'object-hash';

import JokeAnalysisHeader from '../../utils/analysis/JokeAnalysisHeader';  // Component for displaying joke analysis 
import JokeAnalysisComponent from '../../utils/analysis/jokeAnalysisComponent';  // Component for analyzing individual jokes
import BitAnalysisComponent from '../../utils/analysis/bitAnalysisComponent';  // Component for analyzing bits (group of jokes)


const Editor = ({ bits, setBits }) => {
  const [expandedJokes, setExpandedJokes] = useState({});
  const { audienceResponseHashTable, setAudienceResponseHashTable } = useContext(AppContext);

  useEffect(() => {
    setBits(bits || []);
  }, [bits]);





  const handleSaveNewArray = (bitIndex, jokeIndex, trimmedArray, selectedOption) => {
    // Use setBits with a function callback to ensure we're working with the latest bits state
    // console.warn("HandleSaveNewArray called *****")
    setBits(prevBits => {
      const updatedBits = prevBits.map((bit, bIndex) => {
        if (bIndex !== bitIndex) return bit; // Return unchanged bits
  
        // Update the joke at jokeIndex immutably
        return bit.map((joke, jIndex) => {
          if (jIndex !== jokeIndex) return joke; // Return unchanged jokes
  
          const updatedJoke = { ...joke }; // Create a copy of the joke to avoid mutating state
  
          if (selectedOption === 'punchline') {
            updatedJoke.punchline = {
              ...updatedJoke.punchline,
              response: [...trimmedArray] // Update punchline response immutably
            };
          } else if (selectedOption.startsWith('tagline')) {
            const taglineIndex = parseInt(selectedOption.split('-')[1], 10) - 1;
            if (updatedJoke.taglines && updatedJoke.taglines[taglineIndex]) {
              // Update the specific tagline immutably
              updatedJoke.taglines = updatedJoke.taglines.map((tagline, tIndex) => {
                if (tIndex === taglineIndex) {
                  return {
                    ...tagline,
                    punchline: {
                      ...tagline.punchline,
                      response: [...trimmedArray] // Update tagline response immutably
                    }
                  };
                }
                return tagline; // Return unchanged taglines
              });
            } else {
              // console.error('Tagline not found at index:', taglineIndex);
            }
          } else if (selectedOption === 'all') {
            // Split the trimmedArray into segments for punchline and taglines
            const punchlineArray = [];
            const taglineArrays = [];
            let currentArray = punchlineArray;
            let hasHitPositive = false;
  
            // Iterate over the trimmedArray
            for (let i = 0; i < trimmedArray.length; i++) {
              const value = trimmedArray[i];
              if (value > 0) hasHitPositive = true;
  
              if (value === 0 && hasHitPositive) {
                currentArray = taglineArrays;
                hasHitPositive = false;
              }
  
              currentArray.push(value);
            }
  
            // Assign punchline response
            updatedJoke.punchline = {
              ...updatedJoke.punchline,
              response: punchlineArray.length > 0 ? punchlineArray : [0, 0]
            };
  
            // Assign tagline responses
            if (updatedJoke.taglines && updatedJoke.taglines.length > 0) {
              updatedJoke.taglines = updatedJoke.taglines.map((tagline, index) => ({
                ...tagline,
                punchline: {
                  ...tagline.punchline,
                  response: taglineArrays || []
                }
              }));
            }
          }
  
          // After updating the joke, update the audience response hash table here
          const setupText = updatedJoke.setup ? updatedJoke.setup.join('\n') : '';
          const punchlineText = updatedJoke.punchline && updatedJoke.punchline.text ? updatedJoke.punchline.text : '';
          const taglinesText = updatedJoke.taglines && updatedJoke.taglines.length > 0 
            ? updatedJoke.taglines.map(tagline => tagline.punchline.text).join('\n') 
            : '';

// Include setup, punchline, and taglines in the originalText
const normalizedOriginalText = `${setupText}\n${punchlineText}\n${taglinesText}`.trim();



          const jokeKey = hash(normalizedOriginalText);
  
          setAudienceResponseHashTable(prevHashTable => ({
            ...prevHashTable,
            [jokeKey]: {
              ...updatedJoke,
              originalText: normalizedOriginalText
            }
          }));
  
          return updatedJoke; // Return the updated joke
        });
      });
  
      return updatedBits; // Return the updated bits array
    });
  };
  
  
  const handleChange = useCallback(
    (bitIndex, jokeIndex, type, newText) => {
      const newBits = JSON.parse(JSON.stringify(bits));
      const sanitizedText = DOMPurify.sanitize(newText);

      if (type === 'setup') {
        newBits[bitIndex][jokeIndex].setup = sanitizedText.split('\n');
      } else if (type === 'punchline') {
        if (sanitizedText.split('\n').length > 1) {
          alert('Punchlines can only be 1 line. Add the second line either as a setup or as a tagline.');
          return;
        } 
        newBits[bitIndex][jokeIndex].punchline.text = sanitizedText;
      }

      setBits(newBits);
    },
    [bits, setBits]
  );

  const setIsExpandedForJoke = (bitIndex, jokeIndex, isExpanded) => {
    setExpandedJokes((prevState) => ({
      ...prevState,
      [`${bitIndex}-${jokeIndex}`]: isExpanded,
    }));
  };

  const isExpandedForJoke = (bitIndex, jokeIndex) => expandedJokes[`${bitIndex}-${jokeIndex}`] || false;





  const hasValidJokes = Array.isArray(bits) && bits.some(bit =>
    Array.isArray(bit) && bit.some(joke => typeof joke.punchline?.text === 'string' && joke.punchline.text.trim().length > 0)
  );




  
  
  
  if (!hasValidJokes) {
    // console.error("FAILED !hasValidJokes. RETURN NULL");
    return null;
  }

  const flattenedJokes = bits.flat();


  return (
    <div className="editor-main-div">
      <div className="editor-main-page">
        <JokeAnalysisHeader jokes={flattenedJokes} />
        {bits.map((bit, bitIndex) => {
          // Check if the bit contains any jokes with punchlines before rendering the bit
          const hasAssignedPunchlines = Array.isArray(bit) && bit.some(joke => joke.punchline && joke.punchline.text);
  
          if (!hasAssignedPunchlines) {
            // If there are no assigned punchlines, render a message instead
            return (
              <div key={`bit-${bitIndex}`} className="editor-bit-container">
                <p className="no-assigned-punchlines-message">
                  Bit {bitIndex + 1} doesn't have any assigned punchlines
                </p>
              </div>
            );
          }
  
          return (
            <div key={`bit-${bitIndex}`} className="editor-bit-container">
              {bit.map((joke, jokeIndex) => (
                <div key={`joke-${jokeIndex}`} className="editor-joke-container">
                  <div className="editor-joke-content-container">
                    
                    {/* Left Column for the text areas */}
                    <div className="editor-left-column">
                      {/* Textarea for joke setup */}
                      <textarea
                        key={`setup-${jokeIndex}`}
                        className="editor-text-editor editor-setup"
                        value={Array.isArray(joke.setup) ? joke.setup.join('\n') : joke.setup}
                        onChange={(e) => {
                          handleChange(bitIndex, jokeIndex, 'setup', e.target.value);
                          autoResize(e.target);
                        }}
                      />
  
                      {/* Textarea for punchline */}
                      {joke.punchline && (
                        <textarea
                          key={`punchline-${jokeIndex}`}
                          className="editor-text-editor editor-punchline"
                          value={joke.punchline.text || ''}
                          onChange={(e) => {
                            handleChange(bitIndex, jokeIndex, 'punchline', e.target.value);
                            autoResize(e.target);
                          }}
                        />
                      )}
  
                      {/* Render taglines if they exist */}
                      {joke.taglines &&
                        joke.taglines.map((tagline, lineIndex) => (
                          <div key={`tagline-container-${lineIndex}`} className="editor-tagline-container">
                            <textarea
                              className="editor-text-editor editor-tagline-punchline"
                              value={
                                Array.isArray(tagline.punchline.text)
                                  ? tagline.punchline.text.join('\n')
                                  : tagline.punchline.text || ''
                              }
                              onChange={(e) => {
                                const newText = e.target.value;
                                const newBits = JSON.parse(JSON.stringify(bits));
                                newBits[bitIndex][jokeIndex].taglines[lineIndex].punchline.text = newText.split('\n');
                                setBits(newBits);
                              }}
                            />
                          </div>
                        ))}
                    </div>
  
                    {/* Right Column for the button */}
                    <div className="editor-right-column">
                      <button 
                        className="analyze-button"
                        onClick={() => setIsExpandedForJoke(bitIndex, jokeIndex, !isExpandedForJoke(bitIndex, jokeIndex))}
                      >
                        {isExpandedForJoke(bitIndex, jokeIndex) ? '-' : '+'}
                      </button>
                    </div>
                  </div>
  
                  {/* Component for analyzing the joke */}
                  <JokeAnalysisComponent
                    joke={joke}
                    jokeIndex={jokeIndex}
                    bitIndex={bitIndex}
                    isExpanded={isExpandedForJoke(bitIndex, jokeIndex)}
                    setIsExpanded={(isExpanded) => setIsExpandedForJoke(bitIndex, jokeIndex, isExpanded)}
                    onSaveNewArray={handleSaveNewArray}
                  />
                </div>
              ))}
              
              {/* Render the BitAnalysisComponent if the bit is valid */}
              <BitAnalysisComponent bit={bit} />
            </div>
          );
        })}
      </div>
    </div>
  );
  
  
  
  
};

export default Editor;
